import { Fragment, useState, useEffect } from 'react';
import { Menu } from 'react-feather';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Nav, Navbar, Form, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import QuickMenu from 'layouts/QuickMenu';
import AdminTenantDropdown from './AdminTenantDropdown';
import useIsAuthenticated from 'react-auth-kit/hooks/useIsAuthenticated';
import useAuthUser from 'react-auth-kit/hooks/useAuthUser';
import useAuthHeader from 'react-auth-kit/hooks/useAuthHeader';
import useSignOut from 'react-auth-kit/hooks/useSignOut';
import Notification, { handleShowNotification } from 'components/elements/notification/Notification';
import { getRoleId } from 'helper/utils';
import useRole from 'hooks/useRole';

const HeaderDefault = (props) => {
	const { t } = useTranslation(); // useTranslation-Hook
	const [notification, setNotification] = useState({ variant: '', title: '', message: '' });
	const [isButtonDisabled, setIsButtonDisabled] = useState(false);
	const [isLoading, setIsLoading] = useState(false); // State for loading spinner
	const authHeader = useAuthHeader();
	const auth = useAuthUser();
	const isAuthenticated = useIsAuthenticated();
	const signOut = useSignOut();
	const navigate = useNavigate();
	const [showTopBar, setShowTopBar] = useState(false);

	useEffect(() => {
		if (!isAuthenticated) {
			localStorage.removeItem('_auth_state_custom.role_name');
			localStorage.removeItem('_auth_state_custom.tenant_name');
			localStorage.removeItem('_tenant_subscriptions');
			signOut();
			const queryParams = new URLSearchParams({
				notification_type: 'warning',
				notification_title: t('Session expired'),
				notification_message: t('Your Session has expired. Please log in again.'),
			});
			navigate(`/authentication/sign-in?${queryParams.toString()}`);
		}
	}, [isAuthenticated, navigate]);

	const checkUserStatus = async () => {
		if (auth && auth.is_active === 0) {
			setShowTopBar(true);
		} else {
			setShowTopBar(false);
		}
	};

	// Get Role ID and Role
	const roleId = getRoleId();
	const { role, loading } = useRole(roleId);
	const customRoleSelected = localStorage.getItem('_auth_state_custom.role_name');
	const customTenantSelected = localStorage.getItem('_auth_state_custom.tenant_name');

	useEffect(() => {
		checkUserStatus(); // Initial check
		getSubscriptions(); // Fetch subscriptions on mount
		const intervalId = setInterval(() => {
			checkUserStatus();
		}, 60000); // Check every 60 seconds

		return () => clearInterval(intervalId); // Cleanup interval on component unmount
	}, [auth]);

	// API-Call for Subscriptions
	const getSubscriptions = async () => {
		if (!isAuthenticated) {
			return [];
		}

		// API-Call Options
		const options = {
			params: {

			},
			headers: {
				Authorization: authHeader,
				Accept: 'application/json'
			}
		};

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		// Plans
		await axios.get(`${baseURL}/tenants/${auth.tenant_id}/subscriptions`, options)
			.then(response => {
				const responseData = response.data.data; // Extract the data from the response
				localStorage.setItem('_tenant_subscriptions', JSON.stringify(responseData));
				window.dispatchEvent(new CustomEvent('localStorageUpdate'));
			})
			.catch((error) => {
				const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
				handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte versuche es erneut oder kontaktiere den HelpDesk.');
			});
	};

	const handleSendActivationLink = async () => {
		setIsButtonDisabled(true);
		setIsLoading(true); // Set loading to true

		// API-Call Options
		const options = {
			params: {},
			headers: {
				Authorization: authHeader,
				Accept: 'application/json'
			}
		};
		const data = {
			username: auth.username,
			otp_valid_for: "activate_account"
		};

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		axios.post(`${baseURL}/auth/generate-otp`, data, options)
			.then(response => {
				setIsButtonDisabled(false);
				setIsLoading(false); // Stop loading
				handleShowNotification(setNotification, 'info', 'Information', response.data.message + '. Bitte überprüfe dein Postfach und folge den Anweisungen in der E-Mail.');
			})
			.catch((error) => {
				setIsButtonDisabled(false);
				setIsLoading(false); // Stop loading
				const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
				handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Deine Nachricht konnte nicht gesendet werden. Bitte versuche es später erneut oder kontaktiere den Support über hello@haeppy-shopping.com.');
			});
	};

	return (
		<Fragment>
			{notification.show && (
				<Notification variant={notification.variant} title={notification.title} message={notification.message} show={notification.show} setNotification={setNotification} />
			)}
			{showTopBar &&
				<Fragment>
					{/* <!-- Topbar --> */}
					<div className="topbar bg-warning text-dark py-1">
						<div className="d-flex justify-content-between w-100">
							<div className="d-flex align-items-center px-4">
								<span><strong>{t('Warning')}!{' '}</strong>{t('Your Account is not activated')}.{' '}{t('Please check your Mailbox for Activation Mail or')}{' '}</span>
								<Button variant="outline-dark rounded ms-2" size="sm" onClick={handleSendActivationLink} disabled={isButtonDisabled}>
									{isLoading ? (
										<>
											<Spinner animation="border" size="sm" className="me-2" />
											{t('Resend Activation Link')}
										</>
									) : (
										t('Resend Activation Link')
									)}
								</Button>
							</div>
						</div>
					</div>
				</Fragment>
			}
			<Navbar expanded="lg" className="navbar-default">
				<div className="d-flex justify-content-between w-100">
					<div className="d-flex align-items-center">
						<Link
							id="nav-toggle"
							to="#"
							onClick={() => props.data.SidebarToggleMenu(!props.data.showMenu)}
						>
							<Menu size="18px" />
						</Link>
						{/* <!-- Search Form --> */}
						<div className="ms-lg-3 d-none">
							<Form className="d-flex align-items-center">
								<span className="position-absolute ps-3 search-icon">
									<i className="fe fe-search"></i>
								</span>
								<Form.Control type="search" className="form-control ps-6" placeholder="Search Entire Dashboard" />
							</Form>
						</div>
						{/* <!-- Tenant Dropdown --> */}
						{role === 'SYSADMIN' || customRoleSelected || customTenantSelected ? <AdminTenantDropdown /> : null}
					</div>
					<Nav className="navbar-nav navbar-right-wrap ms-auto d-flex align-items-center nav-top-wrap">
						<QuickMenu />
					</Nav>
				</div>
			</Navbar>
		</Fragment>
	);
};

export default HeaderDefault;